import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAuth } from '../../systems/auth-context';
import { Scenario, Scene } from '../../types';
import { validateScenarioStructure, formatValidationErrors } from '../../types/validation';
import { bumpScenarioAssetVersion } from '../../systems/scenario-asset-url';
import {
  createScenario,
  deleteScenario,
  downloadScenario,
  fetchPublishStatus,
  fetchScenarioCatalog,
  importScenarioZip,
  loadScenario,
  pushToMaster,
  revertFromMaster,
  saveScenario,
  ScenarioCatalogItem,
  updateScenarioFromZip,
  type PublishStatus,
} from './scenario-api';
import { parseScenarioZip } from './scenario-zip-import';
import { MetadataEditor, ItemsEditor } from './MetadataEditor';
import { CharacterEditor } from './CharacterEditor';
import { SceneList } from './SceneList';
import { ElementsEditor } from './ElementsEditor';
import { ObjectInspectEditor } from './ObjectInspectEditor';
import { ClickableScreenEditor } from './ClickableScreenEditor';
import { ContentEditor } from './ContentEditor';
import { InlineEditor } from './InlineEditor';
import { TextPlaceholderProvider } from './TextPlaceholderContext';
import { DeveloperMenu } from '../developer-menu';
import { StudioSettingsModal } from './StudioSettingsModal';
import { AssetManager } from './AssetManager';
import { PreviewModal } from './PreviewModal';
import { PreviewAssetLayerToggle } from './PreviewAssetLayerToggle';
import type { ScenarioAssetLayer } from '../../systems/scenario-asset-url';
import { StudioLoader } from './StudioLoader';
import { metadataWithReleaseToProd } from './utils';
import { ensureV2Scenario, stripMissionStartScenes } from '../../systems/migrate-v1-to-v2';
import { getEditorSettings } from '../../systems/editor-settings';
import { useWorkspace } from '../../systems/workspace-context';
import {
  cloneScenarioSnapshot,
  isScenarioDirty,
} from './scenario-equality';
import styles from './scenario-studio.module.css';

type Tab = 'metadata' | 'characters' | 'items' | 'scene' | 'inline' | 'assets';
type ZipUploadMode = 'new' | 'update';

const AUTO_SAVE_MS = 10 * 60 * 1000;

function formatPublishedAt(iso: string | null): string {
  if (!iso) return 'jamais';
  return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

const TAB_META: Record<Tab, { label: string; icon: string }> = {
  metadata: { label: 'Métadonnées', icon: '📋' },
  characters: { label: 'Personnages', icon: '🎭' },
  items: { label: 'Items', icon: '🎒' },
  scene: { label: 'Scènes', icon: '🎬' },
  inline: { label: 'Inline editor', icon: '📝' },
  assets: { label: 'Assets', icon: '🖼️' },
};

type ToastType = 'success' | 'error' | 'info';
interface Toast {
  id: number;
  type: ToastType;
  message: string;
}

interface ScenarioStudioProps {
  onBack: () => void;
}

export const ScenarioStudio: React.FC<ScenarioStudioProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { workspaceId } = useWorkspace();
  const [catalog, setCatalog] = useState<ScenarioCatalogItem[]>([]);
  const [scenarioId, setScenarioId] = useState<string>('');
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [selectedSceneId, setSelectedSceneId] = useState<string | null>(null);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('scene');
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState('Chargement…');
  const [saving, setSaving] = useState(false);
  const [releaseToProdSaving, setReleaseToProdSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadZipModalOpen, setUploadZipModalOpen] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const uploadModeRef = useRef<ZipUploadMode | null>(null);
  const [pushing, setPushing] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [publishStatus, setPublishStatus] = useState<PublishStatus | null>(null);
  const [dirty, setDirty] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewAssetLayer, setPreviewAssetLayer] = useState<ScenarioAssetLayer>('draft');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sceneNavOpen, setSceneNavOpen] = useState(false);
  const [inlineFocusSceneId, setInlineFocusSceneId] = useState<string | null>(null);
  const [lightTheme, setLightTheme] = useState(() => getEditorSettings().lightTheme === true);
  const [validationErrors, setValidationErrors] = useState<string | null>(null);
  const [validationOpen, setValidationOpen] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const savedScenarioRef = useRef<Scenario | null>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const tabScrollRef = useRef<Partial<Record<Tab, number>>>({});

  const setSavedBaseline = (data: Scenario) => {
    savedScenarioRef.current = cloneScenarioSnapshot(data);
  };

  const notify = useCallback((message: string, type: ToastType = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, type, message }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, type === 'error' ? 6000 : 3500);
  }, []);

  const dismissToast = (id: number) => setToasts((prev) => prev.filter((t) => t.id !== id));

  const loadCatalog = useCallback(async () => {
    if (!user) return;
    setCatalogLoading(true);
    try {
      const list = await fetchScenarioCatalog();
      setCatalog(list);
    } finally {
      setCatalogLoading(false);
    }
  }, [user, workspaceId]);

  useEffect(() => {
    loadCatalog().catch(console.error);
  }, [loadCatalog]);

  const loadPublishStatus = useCallback(async (id: string) => {
    if (!user) return;
    try {
      const status = await fetchPublishStatus(id);
      setPublishStatus(status);
    } catch {
      setPublishStatus(null);
    }
  }, [user]);

  const applyScenarioData = (id: string, raw: Scenario) => {
    const data = ensureV2Scenario(raw);
    setScenarioId(id);
    setScenario(data);
    setSelectedSceneId(data.metadata.startScene || data.scenes[0]?.id || null);
    setSavedBaseline(data);
    setDirty(false);
    setValidationErrors(null);
  };

  const catalogHasCurrent = catalog.some((item) => (item.scenarioId || item.id) === scenarioId);

  useEffect(() => {
    if (!scenarioId || !scenario || !catalogHasCurrent) return;
    const title = scenario.metadata.title || scenarioId;
    const version = scenario.metadata.version?.trim() || undefined;
    setCatalog((prev) => {
      const index = prev.findIndex((item) => (item.scenarioId || item.id) === scenarioId);
      if (index === -1) return prev;
      const current = prev[index]!;
      if (current.title === title && (current.version || undefined) === version) return prev;
      const next = [...prev];
      next[index] = { ...current, title, version };
      next.sort((a, b) => a.title.localeCompare(b.title, 'fr', { sensitivity: 'base' }));
      return next;
    });
  }, [scenarioId, scenario?.metadata.title, scenario?.metadata.version, catalogHasCurrent]);

  const openScenario = async (id: string) => {
    if (!user) return;
    setLoading(true);
    setLoadingLabel('Chargement du scénario…');
    try {
      const data = await loadScenario(id);
      applyScenarioData(id, data);
      await loadPublishStatus(id);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Échec du chargement', 'error');
    } finally {
      setLoading(false);
    }
  };

  const runWithLoading = async (label: string, fn: () => Promise<void>) => {
    setLoading(true);
    setLoadingLabel(label);
    try {
      await fn();
    } finally {
      setLoading(false);
    }
  };

  const patchScenario = (next: Scenario) => {
    setScenario(next);
    setDirty(isScenarioDirty(next, savedScenarioRef.current));
  };

  const handleCreate = async () => {
    const id = window.prompt('ID du nouveau scénario (ex: mon-scenario) :');
    if (!id?.trim() || !user) return;
    try {
      await runWithLoading('Création du scénario…', async () => {
        await createScenario(id.trim());
        await loadCatalog();
        const data = await loadScenario(id.trim());
        applyScenarioData(id.trim(), data);
        await loadPublishStatus(id.trim());
      });
      notify('Scénario créé.', 'success');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Échec de la création', 'error');
    }
  };

  const handleUploadClick = () => {
    if (headerBusy) return;
    setUploadZipModalOpen(true);
  };

  const startZipUpload = (mode: ZipUploadMode) => {
    if (mode === 'update' && !scenarioId) return;
    uploadModeRef.current = mode;
    setUploadZipModalOpen(false);
    uploadInputRef.current?.click();
  };

  const handleUploadFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const mode = uploadModeRef.current;
    uploadModeRef.current = null;
    if (!file || !user || !mode) return;

    setUploading(true);
    try {
      const parsed = await parseScenarioZip(file);

      if (mode === 'new') {
        const id = window.prompt('ID du nouveau scénario :', parsed.suggestedId);
        if (!id?.trim()) return;

        await runWithLoading('Import du scénario…', async () => {
          await importScenarioZip(id.trim(), parsed, {
            onProgress: (label) => setLoadingLabel(label),
          });
          await loadCatalog();
          const data = await loadScenario(id.trim());
          applyScenarioData(id.trim(), data);
          await loadPublishStatus(id.trim());
        });
        notify('Scénario importé.', 'success');
        return;
      }

      if (!scenarioId) {
        notify('Ouvrez un scénario avant de le mettre à jour depuis un ZIP.', 'error');
        return;
      }

      if (dirty) {
        const proceed = window.confirm(
          'Des modifications non enregistrées seront écrasées par le ZIP. Continuer ?'
        );
        if (!proceed) return;
      }

      if (parsed.suggestedId !== scenarioId) {
        const proceed = window.confirm(
          `Le ZIP semble correspondre à « ${parsed.suggestedId} » mais vous mettez à jour « ${scenarioId} ». Continuer quand même ?`
        );
        if (!proceed) return;
      }

      await runWithLoading('Mise à jour du scénario…', async () => {
        await updateScenarioFromZip(scenarioId, parsed, {
          onProgress: (label) => setLoadingLabel(label),
        });
        const data = await loadScenario(scenarioId);
        applyScenarioData(scenarioId, data);
        await loadPublishStatus(scenarioId);
      });
      notify('Scénario mis à jour depuis le ZIP.', 'success');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Échec de l’import', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDuplicate = async () => {
    if (!scenarioId || !user) return;
    const id = window.prompt('ID de la copie :', `${scenarioId}-copie`);
    if (!id?.trim()) return;
    try {
      await runWithLoading('Duplication du scénario…', async () => {
        await createScenario(id.trim(), scenarioId);
        await loadCatalog();
        const data = await loadScenario(id.trim());
        applyScenarioData(id.trim(), data);
        await loadPublishStatus(id.trim());
      });
      notify('Scénario dupliqué.', 'success');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Échec de la duplication', 'error');
    }
  };

  const handleDownload = async () => {
    if (!scenarioId || !user) return;
    setDownloading(true);
    try {
      await downloadScenario(scenarioId);
      notify('Scénario téléchargé.', 'success');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Échec du téléchargement', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!scenarioId || !user || !scenario) return;

    const title = scenario.metadata.title || scenarioId;
    if (
      !window.confirm(
        `Supprimer définitivement « ${title} » (${scenarioId}) ?\n\nLe dossier, les assets et les références catalogue seront effacés.`
      )
    ) {
      return;
    }

    setDeleting(true);
    try {
      await deleteScenario(scenarioId);
      setScenario(null);
      setScenarioId('');
      setSelectedSceneId(null);
      savedScenarioRef.current = null;
      setDirty(false);
      setValidationErrors(null);
      await loadCatalog();
      notify('Scénario supprimé.', 'success');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Échec de la suppression', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleReleaseToProdChange = async (releaseToProd: boolean) => {
    if (!scenario || !scenarioId || !user) return;

    const nextScenario = {
      ...scenario,
      metadata: metadataWithReleaseToProd(scenario.metadata, releaseToProd),
    };
    setScenario(nextScenario);
    setReleaseToProdSaving(true);
    try {
      await saveScenario(scenarioId, nextScenario);
      setSavedBaseline(nextScenario);
      setDirty(false);
      notify(
        releaseToProd
          ? 'Scénario publié en production.'
          : 'Scénario retiré de la production (visible admin uniquement).',
        'success'
      );
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Échec de l\'enregistrement', 'error');
      try {
        const data = await loadScenario(scenarioId);
        applyScenarioData(scenarioId, data);
      } catch {
        // keep optimistic state if reload fails
      }
    } finally {
      setReleaseToProdSaving(false);
    }
  };

  const handleSave = async (force = false, options?: { silent?: boolean }) => {
    if (!scenario || !scenarioId || !user) return;

    const validation = validateScenarioStructure(scenario);
    if (!validation.isValid && !force) {
      const msg = formatValidationErrors(validation.errors);
      setValidationErrors(msg);
      if (!window.confirm(`Le scénario contient des erreurs de validation :\n\n${msg}\n\nEnregistrer quand même ?`)) {
        return;
      }
    }

    setSaving(true);
    try {
      const scenarioToSave = stripMissionStartScenes({
        ...scenario,
        metadata: {
          ...scenario.metadata,
          version: bumpScenarioAssetVersion(scenario.metadata.version),
        },
      });
      await saveScenario(scenarioId, scenarioToSave);
      setScenario(scenarioToSave);
      setSavedBaseline(scenarioToSave);
      setDirty(false);
      setValidationErrors(null);
      notify(
        options?.silent ? 'Enregistrement automatique.' : 'Scénario enregistré.',
        options?.silent ? 'info' : 'success'
      );
      await loadPublishStatus(scenarioId);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Échec de l\'enregistrement', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePushToMaster = async () => {
    if (!scenario || !scenarioId || !user) return;

    const validation = validateScenarioStructure(scenario);
    if (!validation.isValid) {
      const msg = formatValidationErrors(validation.errors);
      setValidationErrors(msg);
      notify(`Impossible de publier : le scénario contient des erreurs de validation.`, 'error');
      return;
    }

    if (
      !window.confirm(
        'Publier le brouillon sur master ? Les joueurs verront cette version après le push.'
      )
    ) {
      return;
    }

    setPushing(true);
    try {
      const status = await pushToMaster(scenarioId);
      setPublishStatus(status);
      notify('Scénario publié sur master.', 'success');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Échec du push vers master', 'error');
    } finally {
      setPushing(false);
    }
  };

  const handleRevertFromMaster = async () => {
    if (!scenarioId || !user || !publishStatus?.publishedAt) return;

    const versionLabel = publishStatus.publishedVersion ?? '?';
    if (
      !window.confirm(
        `Toutes les modifications du brouillon seront perdues.\n\nRevenir à la version master v${versionLabel} ?`
      )
    ) {
      return;
    }

    setReverting(true);
    try {
      const { scenario: restored, publishStatus: status } = await revertFromMaster(scenarioId);
      applyScenarioData(scenarioId, restored);
      setPublishStatus(status);
      notify('Brouillon restauré depuis master.', 'success');
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Échec du retour au master', 'error');
    } finally {
      setReverting(false);
    }
  };

  const saveRef = useRef(handleSave);
  saveRef.current = handleSave;

  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const savingRef = useRef(saving);
  savingRef.current = saving;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const releaseToProdSavingRef = useRef(releaseToProdSaving);
  releaseToProdSavingRef.current = releaseToProdSaving;

  useEffect(() => {
    if (!scenario || !scenarioId) return;

    const intervalId = window.setInterval(() => {
      if (getEditorSettings().autoSave === false) return;
      if (!dirtyRef.current) return;
      if (savingRef.current || loadingRef.current || releaseToProdSavingRef.current) return;
      void saveRef.current(true, { silent: true });
    }, AUTO_SAVE_MS);

    return () => clearInterval(intervalId);
  }, [scenario, scenarioId]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        const key = e.key.toLowerCase();
        if (key === 's') {
          e.preventDefault();
          if (scenario && !saving && !loading) {
            saveRef.current().catch(console.error);
          }
          return;
        }
        if (key === 'k' && !e.altKey && scenario && tab !== 'inline') {
          e.preventDefault();
          setSceneNavOpen((prev) => !prev);
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [scenario, saving, loading, tab]);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const handleBack = () => {
    if (dirty && !window.confirm('Des modifications non enregistrées seront perdues. Quitter ?')) {
      return;
    }
    onBack();
  };

  const changeTab = useCallback(
    (next: Tab) => {
      const main = mainRef.current;
      if (main) {
        tabScrollRef.current[tab] = main.scrollTop;
      }
      setTab(next);
    },
    [tab]
  );

  const navigateToSceneInInline = useCallback(
    (sceneId: string) => {
      setSelectedSceneId(sceneId);
      setSelectedElementId(null);
      setInlineFocusSceneId(sceneId);
      changeTab('inline');
      setSceneNavOpen(false);
    },
    [changeTab]
  );

  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main) return;

    const saved = tabScrollRef.current[tab] ?? 0;
    const restore = () => {
      main.scrollTop = saved;
    };

    restore();
    if (main.scrollTop < saved - 1) {
      requestAnimationFrame(() => {
        restore();
      });
    }
  }, [tab]);

  const selectedScene = scenario?.scenes.find((s) => s.id === selectedSceneId) ?? null;
  const sceneIds = scenario?.scenes.map((s) => s.id) ?? [];
  const headerBusy =
    loading ||
    saving ||
    catalogLoading ||
    releaseToProdSaving ||
    deleting ||
    downloading ||
    uploading ||
    pushing ||
    reverting;

  const canPushToMaster = Boolean(
    scenarioId && publishStatus?.hasUnpublishedChanges && !dirty
  );
  const canRevertFromMaster = Boolean(
    scenarioId && publishStatus?.publishedAt && publishStatus.hasUnpublishedChanges
  );

  return (
    <div className={styles.studio} data-theme={lightTheme ? 'light' : undefined}>
      <div className={styles.workspace}>
        <div ref={mainRef} className={styles.scrollBody}>
        <header className={styles.header}>
          <button type="button" className={styles.btnGhost} onClick={handleBack} disabled={headerBusy}>← Retour</button>
          <h1>Scenario Studio</h1>
          <div className={styles.headerActions}>
            <select
              className={styles.select}
              value={scenarioId}
              onChange={(e) => e.target.value && openScenario(e.target.value)}
              disabled={headerBusy}
            >
              <option value="">
                {catalogLoading ? 'Chargement du catalogue…' : 'Choisir un scénario…'}
              </option>
              {catalog.map((s) => (
                <option key={s.id} value={s.scenarioId || s.id}>
                  {s.version ? `${s.title} (${s.version})` : s.title}
                </option>
              ))}
            </select>
            <button type="button" className={styles.btnSecondary} onClick={handleCreate} disabled={headerBusy} title="Créer un nouveau scénario">
              Nouveau
            </button>
            <button
              type="button"
              className={`${styles.btnSecondary} ${uploading ? styles.btnLoading : ''}`}
              onClick={handleUploadClick}
              disabled={headerBusy}
              title="Importer ou mettre à jour un scénario depuis un fichier ZIP"
            >
              {uploading ? (
                <>
                  <span className={styles.spinner} /> Import…
                </>
              ) : (
                'Upload ZIP'
              )}
            </button>
            <input
              ref={uploadInputRef}
              type="file"
              accept=".zip,application/zip"
              hidden
              onChange={handleUploadFileChange}
            />
            <button type="button" className={styles.btnSecondary} onClick={handleDuplicate} disabled={!scenarioId || headerBusy} title="Dupliquer le scénario courant">
              Dupliquer
            </button>
            <button
              type="button"
              className={`${styles.btnDanger} ${deleting ? styles.btnLoading : ''}`}
              onClick={() => handleDelete()}
              disabled={!scenarioId || headerBusy}
              title="Supprimer définitivement le scénario courant"
            >
              {deleting ? (
                <>
                  <span className={styles.spinner} /> Suppression…
                </>
              ) : (
                'Supprimer'
              )}
            </button>
            <PreviewAssetLayerToggle
              value={previewAssetLayer}
              onChange={setPreviewAssetLayer}
              disabled={!scenario || loading}
            />
            <button type="button" className={styles.btnSecondary} onClick={() => setPreviewOpen(true)} disabled={!scenario || loading} title="Prévisualiser le scénario">
              Aperçu
            </button>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => setSettingsOpen(true)}
              disabled={headerBusy}
              title="Paramètres de l'éditeur (clé API Anthropic)"
            >
              ⚙️ Paramètres
            </button>
            <button
              type="button"
              className={`${styles.btnSecondary} ${downloading ? styles.btnLoading : ''}`}
              onClick={() => handleDownload()}
              disabled={!scenarioId || headerBusy}
              title="Télécharger le dossier complet du scénario (zip, version serveur)"
            >
              {downloading ? (
                <>
                  <span className={styles.spinner} /> Téléchargement…
                </>
              ) : (
                'Télécharger scénario'
              )}
            </button>
            {scenarioId && (
              <div className={styles.publishPanel}>
                <div className={styles.publishMeta}>
                  <span>
                    Master :{' '}
                    {publishStatus?.publishedVersion
                      ? `v${publishStatus.publishedVersion}`
                      : 'non publié'}
                  </span>
                  <span>· pushé le {formatPublishedAt(publishStatus?.publishedAt ?? null)}</span>
                  {publishStatus?.hasUnpublishedChanges && (
                    <span className={styles.publishWarning}>Modifications non publiées</span>
                  )}
                </div>
                <div className={styles.publishActions}>
                  <button
                    type="button"
                    className={`${styles.btnSecondary} ${reverting ? styles.btnLoading : ''}`}
                    onClick={() => handleRevertFromMaster()}
                    disabled={!canRevertFromMaster || headerBusy}
                    title="Restaurer le brouillon depuis la dernière version master"
                  >
                    {reverting ? (
                      <>
                        <span className={styles.spinner} /> Retour…
                      </>
                    ) : (
                      'Revenir au master'
                    )}
                  </button>
                  <button
                    type="button"
                    className={`${styles.btnPrimary} ${pushing ? styles.btnLoading : ''}`}
                    onClick={() => handlePushToMaster()}
                    disabled={!canPushToMaster || headerBusy}
                    title="Publier le brouillon sur master pour les joueurs"
                  >
                    {pushing ? (
                      <>
                        <span className={styles.spinner} /> Push…
                      </>
                    ) : (
                      'Push to master'
                    )}
                  </button>
                </div>
              </div>
            )}
            <button
              type="button"
              className={`${styles.btnPrimary} ${saving ? styles.btnLoading : ''}`}
              onClick={() => handleSave()}
              disabled={!scenario || saving || loading}
              title="Enregistrer (Ctrl+S)"
            >
              {saving ? (
                <>
                  <span className={styles.spinner} /> Enregistrement…
                </>
              ) : (
                <>
                  {dirty && <span className={styles.dirtyDot} aria-hidden />}
                  Enregistrer
                  <span className={styles.kbdHint}>Ctrl+S</span>
                </>
              )}
            </button>
          </div>
        </header>

        {(loading || saving) && (
          <StudioLoader
            label={saving ? 'Enregistrement sur le serveur…' : loadingLabel}
            variant="bar"
          />
        )}
        {validationErrors && (
          <div className={styles.validationBanner}>
            <span>⚠️</span>
            <div style={{ flex: 1 }}>
              <strong>Validation :</strong>{' '}
              {validationOpen
                ? null
                : `${validationErrors.slice(0, 160)}${validationErrors.length > 160 ? '…' : ''}`}
              {validationOpen && <pre>{validationErrors}</pre>}
            </div>
            {validationErrors.length > 160 && (
              <button
                type="button"
                className={styles.validationToggle}
                onClick={() => setValidationOpen((v) => !v)}
              >
                {validationOpen ? 'Réduire' : 'Détails'}
              </button>
            )}
          </div>
        )}

        {!scenario ? (
          <div className={styles.empty}>
            {catalogLoading ? (
              <StudioLoader label="Chargement du catalogue…" />
            ) : (
              <>
                <div className={styles.emptyIllustration} aria-hidden>🎬</div>
                <p>Sélectionnez un scénario dans la liste en haut, ou créez-en un nouveau pour commencer l&apos;édition.</p>
              </>
            )}
          </div>
        ) : (
          <TextPlaceholderProvider scenarioId={scenarioId}>
          <>
            <nav className={styles.tabs}>
              {(['metadata', 'characters', 'items', 'scene', 'inline', 'assets'] as Tab[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`${styles.tab} ${tab === t ? styles.tabActive : ''}`}
                  onClick={() => changeTab(t)}
                  title={TAB_META[t].label}
                >
                  <span className={styles.tabIcon} aria-hidden>{TAB_META[t].icon}</span>
                  {TAB_META[t].label}
                </button>
              ))}
            </nav>

            <main className={`${styles.main} ${loading ? styles.mainBusy : ''}`}>
            {loading && <StudioLoader label={loadingLabel} variant="overlay" />}
            {tab === 'metadata' && (
              <MetadataEditor
                scenarioId={scenarioId}
                metadata={scenario.metadata}
                sceneIds={sceneIds}
                scenes={scenario.scenes}
                onChange={(metadata) => patchScenario({ ...scenario, metadata })}
                onReleaseToProdChange={handleReleaseToProdChange}
                releaseToProdSaving={releaseToProdSaving}
              />
            )}
            {tab === 'characters' && (
              <CharacterEditor
                scenarioId={scenarioId}
                characters={scenario.metadata.characters ?? []}
                onChange={(characters) =>
                  patchScenario({ ...scenario, metadata: { ...scenario.metadata, characters } })
                }
              />
            )}
            {tab === 'items' && (
              <ItemsEditor
                scenarioId={scenarioId}
                items={scenario.items}
                onChange={(items) => patchScenario({ ...scenario, items })}
              />
            )}
            {tab === 'assets' && <AssetManager scenarioId={scenarioId} />}
            <div hidden={tab !== 'inline'}>
              <InlineEditor
                scenarioId={scenarioId}
                scenario={scenario}
                onChange={patchScenario}
                onOpenSettings={() => setSettingsOpen(true)}
                isActive={tab === 'inline'}
                previewAssetLayer={previewAssetLayer}
                onPreviewAssetLayerChange={setPreviewAssetLayer}
                focusSceneId={inlineFocusSceneId}
                onFocusSceneConsumed={() => setInlineFocusSceneId(null)}
                onOpenSceneInEditor={(sceneId, elementId) => {
                  setSelectedSceneId(sceneId);
                  setSelectedElementId(elementId ?? null);
                  changeTab('scene');
                }}
              />
            </div>
            {tab === 'scene' && (
              <div className={styles.sceneWorkspace}>
                <aside className={styles.leftCol}>
                  <SceneList
                    scenarioId={scenarioId}
                    scenario={scenario}
                    selectedSceneId={selectedSceneId}
                    onSelectScene={(id) => {
                      setSelectedSceneId(id);
                      setSelectedElementId(null);
                    }}
                    onChange={patchScenario}
                  />
                </aside>
                <section className={styles.centerCol}>
                  {selectedScene ? (
                    selectedScene.type === 'observation' ? (
                      <ObjectInspectEditor
                        scenarioId={scenarioId}
                        scene={selectedScene}
                        sceneIds={sceneIds}
                        selectedElementId={selectedElementId}
                        onSelectedElementIdChange={setSelectedElementId}
                        onChange={(scene) =>
                          patchScenario({
                            ...scenario,
                            scenes: scenario.scenes.map((s) =>
                              s.id === selectedScene.id ? scene : s
                            ),
                          })
                        }
                      />
                    ) : selectedScene.type === 'clickableScreen' ? (
                      <ClickableScreenEditor
                        scenarioId={scenarioId}
                        scene={selectedScene}
                        sceneIds={sceneIds}
                        onChange={(scene) =>
                          patchScenario({
                            ...scenario,
                            scenes: scenario.scenes.map((s) =>
                              s.id === selectedScene.id ? scene : s
                            ),
                          })
                        }
                      />
                    ) : (
                      <>
                        <ContentEditor
                          scenarioId={scenarioId}
                          scenario={scenario}
                          scene={selectedScene}
                          onScenePatch={(patch) =>
                            patchScenario({
                              ...scenario,
                              scenes: scenario.scenes.map((s) =>
                                s.id === selectedScene.id ? ({ ...s, ...patch } as Scene) : s
                              ),
                            })
                          }
                        />
                        <ElementsEditor
                          scenarioId={scenarioId}
                          scene={selectedScene}
                          selectedElementId={selectedElementId}
                          onSelectedElementIdChange={setSelectedElementId}
                          onChange={(elements) =>
                            patchScenario({
                              ...scenario,
                              scenes: scenario.scenes.map((s) =>
                                s.id === selectedScene.id ? { ...s, elements } : s
                              ),
                            })
                          }
                        />
                      </>
                    )
                  ) : (
                    <p className={styles.hint}>Sélectionnez une scène.</p>
                  )}
                </section>
              </div>
            )}
          </main>
        </>
        {previewOpen ? (
          <PreviewModal
            scenario={scenario}
            scenarioId={scenarioId}
            startSceneId={selectedSceneId ?? undefined}
            assetLayer={previewAssetLayer}
            onClose={() => setPreviewOpen(false)}
          />
        ) : null}
        </TextPlaceholderProvider>
        )}
        </div>
      </div>

      {sceneNavOpen && scenario && (
        <DeveloperMenu
          scenario={scenario}
          currentSceneId={selectedSceneId ?? undefined}
          onNavigateToScene={navigateToSceneInInline}
          onClose={() => setSceneNavOpen(false)}
          title="Aller à la scène"
          subtitle="Naviguez dans l'éditeur inline · Ctrl+K"
          currentBadgeLabel="Sélectionnée"
        />
      )}

      <StudioSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSaved={(settings) => setLightTheme(settings.lightTheme === true)}
      />

      {uploadZipModalOpen && (
        <div
          className={styles.modalOverlay}
          onClick={() => !uploading && setUploadZipModalOpen(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <header className={styles.modalHeader}>
              <h3>Upload ZIP</h3>
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => setUploadZipModalOpen(false)}
                disabled={uploading}
              >
                ×
              </button>
            </header>
            <div className={styles.modalBody}>
              <p className={styles.hint} style={{ marginBottom: '1rem' }}>
                Choisissez comment appliquer le fichier ZIP.
              </p>
              <div className={styles.zipUploadChoices}>
                <button
                  type="button"
                  className={styles.zipUploadChoice}
                  onClick={() => startZipUpload('new')}
                  disabled={uploading}
                >
                  <strong>Nouveau scénario</strong>
                  <span>Crée un scénario et importe le contenu du ZIP.</span>
                </button>
                <button
                  type="button"
                  className={`${styles.zipUploadChoice} ${!scenarioId ? styles.zipUploadChoiceDisabled : ''}`}
                  onClick={() => startZipUpload('update')}
                  disabled={uploading || !scenarioId}
                  title={
                    scenarioId
                      ? 'Remplace le brouillon du scénario ouvert'
                      : 'Ouvrez un scénario pour activer cette option'
                  }
                >
                  <strong>Mettre à jour</strong>
                  <span>
                    {scenarioId
                      ? `Remplace le brouillon de « ${scenario?.metadata.title || scenarioId} ». Les assets identiques (même chemin et taille) ne sont pas réimportés.`
                      : 'Ouvrez un scénario dans l’éditeur pour activer cette option.'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toasts.length > 0 && (
        <div className={styles.toastContainer} role="status" aria-live="polite">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`${styles.toast} ${
                t.type === 'success'
                  ? styles.toastSuccess
                  : t.type === 'error'
                    ? styles.toastError
                    : styles.toastInfo
              }`}
            >
              <span className={styles.toastIcon} aria-hidden>
                {t.type === 'success' ? '✅' : t.type === 'error' ? '⛔' : 'ℹ️'}
              </span>
              <span className={styles.toastMessage}>{t.message}</span>
              <button
                type="button"
                className={styles.toastClose}
                onClick={() => dismissToast(t.id)}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
